import { updateJobDetail } from '@/domains/jobs/api/jobDetailApi';
import type { Job } from '@/domains/jobs/components/JobCard';
import { queryKeys } from '@/lib/query-client';
import { showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  Clock,
  Edit2,
  ExternalLink,
  MapPin,
  Save,
  ShieldCheck,
  User,
  X,
} from 'lucide-react';
import Map, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useMemo, useState } from 'react';

interface JobDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job | null;
  onViewFullDetails?: (jobId: string) => void;
  onViewFieldWorker?: (jobId: string) => void;
}

const MAPBOX_TOKEN =
  import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || import.meta.env.VITE_MAPBOX_TOKEN || '';
export function JobDetailsModal({
  open,
  onOpenChange,
  job,
  onViewFullDetails,
  onViewFieldWorker,
}: JobDetailsModalProps) {
  const queryClient = useQueryClient();
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const [editedDuration, setEditedDuration] = useState(job?.duration || job?.estimatedHours || 4);
  const [dcrConfirmOpen, setDcrConfirmOpen] = useState(false);
  const [dcrResultOpen, setDcrResultOpen] = useState(false);
  const [isSavingDuration, setIsSavingDuration] = useState(false);
  const currentDuration = job?.duration || job?.estimatedHours || 4;

  useEffect(() => {
    if (job) {
      setEditedDuration(job.duration || job.estimatedHours || 4);
    }
  }, [job]);

  const coords = useMemo(() => {
    if (!job?.coordinates) return null;
    const parts = job.coordinates.split(',').map((s) => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      if (Math.abs(parts[0]) <= 90) return { lat: parts[0], lng: parts[1] };
      return { lat: parts[1], lng: parts[0] };
    }
    return null;
  }, [job?.coordinates]);

  if (!open || !job) return null;

  const handleClose = () => {
    onOpenChange(false);
  };

  const wouldTriggerDcr = currentDuration > 0 && editedDuration > currentDuration * 1.5;

  const handleSaveDuration = async () => {
    const jobId = Number(job?.id);
    if (!jobId || !Number.isFinite(jobId) || jobId <= 0) return;
    if (wouldTriggerDcr) {
      setDcrConfirmOpen(true);
      return;
    }
    await doSaveDuration();
  };

  const doSaveDuration = async () => {
    const jobId = Number(job?.id);
    if (!jobId || !Number.isFinite(jobId) || jobId <= 0) return;
    setIsSavingDuration(true);
    try {
      const result = await updateJobDetail(jobId, { duration: editedDuration });
      setIsEditingDuration(false);
      setDcrConfirmOpen(false);
      // Invalidate planner and job detail queries so status updates
      queryClient.invalidateQueries({ queryKey: [...queryKeys.planner.all], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) });
      if ((result as any)?._durationChallenge) {
        setDcrResultOpen(true);
      } else {
        showSuccessToast('Duration updated');
      }
    } catch {
      // error handled by global handler
    }
    setIsSavingDuration(false);
  };

  const handleCancelEdit = () => {
    setEditedDuration(job?.duration || job?.estimatedHours || 4);
    setIsEditingDuration(false);
  };

  const statusConfig = {
    pending: {
      label: 'Not Started',
      color: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100',
    },
    'in-progress': {
      label: 'In Progress',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
    },
    completed: {
      label: 'Completed',
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
    },
    urgent: {
      label: 'Urgent',
      color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
    },
  };

  const priorityConfig = {
    high: { label: 'High Priority', color: 'text-red-600 dark:text-red-400' },
    medium: { label: 'Medium Priority', color: 'text-yellow-600 dark:text-yellow-400' },
    low: { label: 'Low Priority', color: 'text-blue-600 dark:text-blue-400' },
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

        {/* Modal - Wider to accommodate map */}
        <div className="relative bg-background border-2 border-border rounded-xl shadow-2xl w-full max-w-[900px] overflow-hidden">
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-border">
            <div className="flex-1 pr-4">
              <h2 className="text-lg font-semibold text-foreground mb-1">{job.title}</h2>
              {job.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">{job.description}</p>
              )}
              {job.client && (
                <p className="text-xs text-muted-foreground mt-1">Client: {job.client}</p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              className="border-border hover:bg-accent flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Content - Two columns */}
          <div className="grid grid-cols-2 gap-6 p-5">
            {/* Left Column - Job Info */}
            <div className="space-y-4">
              {/* Status and Priority Badges */}
              <div className="flex items-center gap-2">
                <Badge className={statusConfig[job.status as keyof typeof statusConfig].color}>
                  {statusConfig[job.status as keyof typeof statusConfig].label}
                </Badge>
                <Badge
                  variant="outline"
                  className={`border-border ${priorityConfig[job.priority as keyof typeof priorityConfig].color}`}
                >
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {priorityConfig[job.priority as keyof typeof priorityConfig].label}
                </Badge>
              </div>

              {/* Key Information */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-0.5">Location</p>
                    <p className="text-sm text-foreground">{job.location || 'Not specified'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-0.5">Scheduled</p>
                    <p className="text-sm text-foreground">
                      {job.scheduledDate
                        ? new Date(job.scheduledDate).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        : 'Not scheduled'}
                      {job.scheduledTime && ` at ${job.scheduledTime}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-0.5">Duration</p>
                    {isEditingDuration ? (
                      <div className="flex items-center">
                        <Input
                          type="number"
                          value={editedDuration}
                          onChange={(e) => setEditedDuration(Number(e.target.value))}
                          className="w-16"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSaveDuration}
                          className="ml-2"
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEdit}
                          className="ml-2"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <p className="text-sm text-foreground">
                          {editedDuration >= 24
                            ? `${editedDuration} hours (${Math.floor(editedDuration / 24)} day${Math.floor(editedDuration / 24) > 1 ? 's' : ''})`
                            : `${editedDuration} hours`}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsEditingDuration(true)}
                          className="ml-2"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-0.5">Assignee</p>
                    <p className="text-sm text-foreground">{job.assignee || 'Unassigned'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground mb-0.5">
                      Permit &amp; Reinstatement
                    </p>
                    {job.permitRequired || job.reinstatementRequired || job.permitStatusName ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {job.permitRequired && (
                          <Badge
                            variant="outline"
                            className="text-orange-600 border-orange-600 gap-1 text-xs"
                          >
                            <ShieldCheck className="size-3" />
                            Permit
                          </Badge>
                        )}
                        {job.reinstatementRequired && (
                          <Badge
                            variant="outline"
                            className="text-blue-600 border-blue-600 text-xs"
                          >
                            Reinstatement
                          </Badge>
                        )}
                        {job.permitStatusName && (
                          <span className="text-sm text-foreground">{job.permitStatusName}</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Not required</p>
                    )}
                  </div>
                </div>
              </div>

              {job.client && (
                <div className="bg-muted/50 rounded-lg p-3 border border-border">
                  <p className="text-xs text-muted-foreground mb-1">Client</p>
                  <p className="text-sm font-medium text-foreground">{job.client}</p>
                </div>
              )}

              {job.tags && job.tags.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {job.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs border-border">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Map */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Job Location</p>
              <div className="rounded-lg overflow-hidden border-2 border-border shadow-lg relative h-[300px]">
                {coords && MAPBOX_TOKEN ? (
                  <Map
                    mapboxAccessToken={MAPBOX_TOKEN}
                    initialViewState={{
                      longitude: coords.lng,
                      latitude: coords.lat,
                      zoom: 15,
                    }}
                    style={{ width: '100%', height: '300px' }}
                    mapStyle="mapbox://styles/mapbox/streets-v11"
                  >
                    <Marker longitude={coords.lng} latitude={coords.lat} color="#E84A2B" />
                  </Map>
                ) : (
                  <div className="h-full flex items-center justify-center bg-muted/30">
                    <div className="text-center">
                      <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No coordinates available</p>
                    </div>
                  </div>
                )}

                {/* Location label */}
                <div className="absolute bottom-4 left-4 right-4 bg-white dark:bg-gray-800 rounded-lg p-3 shadow-lg border border-border z-10">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#E84A2B] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate">
                        {job.location || 'Location not specified'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer with Actions */}
          <div className="p-5 border-t border-border bg-muted/30 flex items-center justify-between">
            <Button variant="outline" onClick={handleClose} className="border-border">
              Close
            </Button>
            <div className="flex items-center gap-2">
              <Button
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                onClick={() => {
                  handleClose();
                  if (onViewFullDetails) {
                    onViewFullDetails(job.id);
                  }
                }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Full Details
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Duration Challenge Confirmation */}
      <Dialog open={dcrConfirmOpen} onOpenChange={setDcrConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <DialogTitle>Duration Challenge Required</DialogTitle>
                <DialogDescription className="mt-1">
                  This duration change exceeds the current estimate by more than 50% and will
                  require approval from Ignite Core.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-amber-700 dark:text-amber-300">Current Duration</span>
              <span className="font-medium text-amber-900 dark:text-amber-100">
                {currentDuration} hours
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-amber-700 dark:text-amber-300">New Duration</span>
              <span className="font-medium text-amber-900 dark:text-amber-100">
                {editedDuration} hours
              </span>
            </div>
            <div className="flex justify-between pt-1 border-t border-amber-200 dark:border-amber-700">
              <span className="text-amber-700 dark:text-amber-300">Increase</span>
              <span className="font-medium text-amber-900 dark:text-amber-100">
                {currentDuration > 0
                  ? Math.round(((editedDuration - currentDuration) / currentDuration) * 100)
                  : 0}
                %
              </span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            The job will be placed on hold until the challenge is approved or rejected. Are you
            sure?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDcrConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={doSaveDuration}
              disabled={isSavingDuration}
            >
              {isSavingDuration ? 'Sending...' : 'Send Duration Challenge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duration Challenge Sent */}
      <Dialog open={dcrResultOpen} onOpenChange={setDcrResultOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Duration Challenge Sent</DialogTitle>
            <DialogDescription>
              The Duration Challenge Request has been sent to Ignite Core. The job is now on hold
              pending approval.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDcrResultOpen(false)}>Understood</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
